import { useEffect, useState } from 'react';
import { FileText, Download, Eye, Calendar, Loader2, Plus, Info, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { patentApi } from '@/services/api';
import { useSessionStore, useNotificationStore } from '@/stores';

interface ProsecutionDoc {
  docId: string;
  code: string;
  description: string;
  date: string;
  mailDate: string | null;
  pageCount: number | null;
  rawDoc: any;
  downloadOptions: {
    pdf?: string;
    xml?: string;
  };
}

interface ProsecutionDocumentsProps {
  applicationNumber: string;
  onDocumentClick?: (doc: any) => void;
}

export function ProsecutionDocuments({ applicationNumber, onDocumentClick }: ProsecutionDocumentsProps) {
  const [docs, setDocs] = useState<ProsecutionDoc[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedDoc, setExpandedDoc] = useState<string | null>(null);
  const [downloadingDocs, setDownloadingDocs] = useState<Set<string>>(new Set());
  const [addingDocs, setAddingDocs] = useState<Set<string>>(new Set());

  const sessionDocuments = useSessionStore(state => state.sessionDocuments);
  const { addNotification } = useNotificationStore();

  useEffect(() => {
    const loadDocs = async () => {
      if (!applicationNumber) return;

      setLoading(true);
      setError(null);
      try {
        const result = await patentApi.fetchProsecutionDocInfo(applicationNumber);
        const parsed =
          result?.pros_info
            ?.filter((doc: any) => doc.documentIdentifier)
            .map((doc: any) => {
              const pdf = doc.downloadOptionBag?.find((d: any) => d.mimeTypeIdentifier === 'PDF');
              const xml = doc.downloadOptionBag?.find((d: any) => d.mimeTypeIdentifier === 'XML');

              return {
                docId: doc.documentIdentifier,
                code: doc.documentCode,
                description: doc.documentCodeDescriptionText || doc.documentCode,
                date: doc.officialDate ? new Date(doc.officialDate).toLocaleDateString() : 'N/A',
                mailDate: doc.mailDate ? new Date(doc.mailDate).toLocaleDateString() : null,
                pageCount: doc.pageCount || null,
                rawDoc: doc,
                downloadOptions: {
                  pdf: pdf?.downloadUrl || pdf?.downloadURI,
                  xml: xml?.downloadUrl || xml?.downloadURI,
                },
              };
            }) || [];

        // Sort by date (most recent first)
        parsed.sort((a: ProsecutionDoc, b: ProsecutionDoc) => {
          if (!a.date || a.date === 'N/A') return 1;
          if (!b.date || b.date === 'N/A') return -1;
          return new Date(b.date).getTime() - new Date(a.date).getTime();
        });

        setDocs(parsed);
      } catch (e) {
        console.error('Failed to fetch prosecution documents:', e);
        setError('Failed to load prosecution documents');
        setDocs([]);
      } finally {
        setLoading(false);
      }
    };

    loadDocs();
  }, [applicationNumber]);

  const createShortFilename = (appNum: string, docId: string, description: string, format: string): string => {
    // Sanitize and shorten filename to prevent issues
    const sanitizedAppNum = appNum.replace(/[^a-zA-Z0-9]/g, '');
    const sanitizedDocId = docId.replace(/[^a-zA-Z0-9-_]/g, '');

    // Try to use a shortened description
    const sanitizedDesc = description
      .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special chars
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .substring(0, 30); // Limit description to 30 chars

    // Construct filename: appNum-docId-shortDesc.ext
    // Max 80 chars total before extension
    const baseFilename = `${sanitizedAppNum}-${sanitizedDocId}-${sanitizedDesc}`;
    const maxBaseLength = 80;
    const truncatedBase = baseFilename.length > maxBaseLength
      ? baseFilename.substring(0, maxBaseLength)
      : baseFilename;

    return `${truncatedBase}.${format}`;
  };

  const downloadViaBlob = async (url: string, filename: string) => {
    // Fetch the file content
    const response = await fetch(url);
    const blob = await response.blob();

    // Create download link
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  };

  const handleDownload = async (docId: string, docObj: any, format: string = 'pdf') => {
    const downloadKey = `${docId}-${format}`;

    // Prevent multiple downloads of the same document
    if (downloadingDocs.has(downloadKey)) return;

    setDownloadingDocs((prev) => new Set([...prev, downloadKey]));

    try {
      // Find the document to get its description
      const doc = docs.find(d => d.docId === docId);
      const description = doc?.description || 'document';

      // Check if filename would be too long (> 100 chars)
      const potentialFilename = `${applicationNumber}-${description}`;
      const needsShortening = potentialFilename.length > 100;

      // Use the backend API to get the document (always get fresh URL to avoid expiration)
      const result = await patentApi.fetchProsecutionDoc(applicationNumber, docId, docObj, true);

      if (result && result.url) {
        // Use direct URL if available
        if (needsShortening) {
          // Download via blob with shortened filename
          const shortFilename = createShortFilename(applicationNumber, docId, description, format);
          await downloadViaBlob(result.url, shortFilename);
        } else {
          // Normal download - let browser handle it
          window.open(result.url, '_blank');
        }
      } else if (result && result.file_content) {
        // Create blob from base64 content if provided
        const mimeType = format === 'pdf' ? 'application/pdf' : 'application/xml';
        const byteCharacters = atob(result.file_content);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });

        // Create download link with controlled filename
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        const filename = createShortFilename(applicationNumber, docId, description, format);
        link.download = filename;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        // Fallback: try to get download URL from backend (always get fresh URL to avoid expiration)
        const urlResult = await patentApi.getProsecutionDocUrl(applicationNumber, docId, true);
        if (urlResult && urlResult.download_url) {
          if (needsShortening) {
            const shortFilename = createShortFilename(applicationNumber, docId, description, format);
            await downloadViaBlob(urlResult.download_url, shortFilename);
          } else {
            window.open(urlResult.download_url, '_blank');
          }
        } else {
          throw new Error('Unable to retrieve document');
        }
      }
    } catch (error: any) {
      console.error('Failed to download document:', error);
      alert(`Failed to download document: ${error.message}`);
    } finally {
      setDownloadingDocs((prev) => {
        const newSet = new Set(prev);
        newSet.delete(downloadKey);
        return newSet;
      });
    }
  };

  const handleAddToActiveDocuments = async (doc: ProsecutionDoc) => {
    const prosDocId = `${applicationNumber}-${doc.docId}`;

    // Check if already added
    const alreadyAdded = (sessionDocuments as any[])?.some(
      (d: any) => d.resource_id === prosDocId && d.resource_type === 'prosecution_document'
    );

    if (alreadyAdded) {
      addNotification({
        type: 'info',
        title: 'Already Added',
        message: `${doc.description} is already in your active documents.`,
      });
      return;
    }

    setAddingDocs((prev) => new Set([...prev, doc.docId]));

    try {
      await patentApi.addProsecutionDocToSession(applicationNumber, doc.docId, doc.description);

      addNotification({
        type: 'success',
        title: 'Document Added',
        message: `${doc.description} has been added to active documents.`,
      });

      // Trigger storage update event to refresh session state
      window.dispatchEvent(new Event('storage-update'));
    } catch (error: any) {
      console.error('Failed to add to active documents:', error);
      addNotification({
        type: 'error',
        title: 'Failed to Add',
        message: 'Failed to add document to active documents. Please try again.',
      });
    } finally {
      setAddingDocs((prev) => {
        const newSet = new Set(prev);
        newSet.delete(doc.docId);
        return newSet;
      });
    }
  };

  const getDocumentTypeColor = (code: string) => {
    // Color coding based on document type
    if (code?.includes('CT')) return 'text-red-600'; // Office Actions
    if (code?.includes('A.') || code?.includes('REM')) return 'text-blue-600'; // Responses
    if (code?.includes('NOA') || code?.includes('IFEE')) return 'text-green-600'; // Allowance/Issue
    if (code?.includes('SPEC') || code?.includes('CLM') || code?.includes('DRW'))
      return 'text-purple-600'; // Application docs
    return 'text-gray-600'; // Default
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Prosecution Documents</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Loading prosecution documents...</span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Prosecution Documents</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-red-600">
            <p>{error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!docs.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Prosecution Documents</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            <FileText className="mx-auto text-4xl mb-2 opacity-50" />
            <p>No prosecution documents found for this application.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Prosecution Documents ({docs.length})</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3 max-h-[calc(100vh-350px)] overflow-y-auto">
          {docs.map((doc, index) => (
            <div
              key={doc.docId || index}
              className="border border-gray-200 rounded-lg p-4 bg-gray-50 transition-colors"
            >
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className={`w-4 h-4 ${getDocumentTypeColor(doc.code)}`} />
                    <span className="font-medium text-sm text-gray-900">
                      {doc.description}
                    </span>
                    <span className="text-xs px-2 py-1 rounded bg-gray-200 text-gray-700">
                      {doc.code}
                    </span>
                  </div>

                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      <span>Filed: {doc.date}</span>
                    </div>
                    {doc.mailDate && (
                      <div className="flex items-center gap-1">
                        <span>Mailed: {doc.mailDate}</span>
                      </div>
                    )}
                    {doc.pageCount && <span>{doc.pageCount} pages</span>}
                  </div>
                </div>

                <div className="flex gap-1 ml-2" onClick={(e) => e.stopPropagation()}>
                  {(() => {
                    // Check if document is already in active documents
                    const prosDocId = `${applicationNumber}-${doc.docId}`;
                    const isInActiveDocuments = (sessionDocuments as any[])?.some(
                      (d: any) => d.resource_id === prosDocId && d.resource_type === 'pros_doc'
                    );

                    if (isInActiveDocuments) {
                      return (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled
                          className="h-8 w-8 p-0"
                          title="Already in Active Documents"
                        >
                          <Check className="w-4 h-4 text-green-600" />
                        </Button>
                      );
                    }

                    return (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleAddToActiveDocuments(doc)}
                        disabled={addingDocs.has(doc.docId)}
                        className="h-8 w-8 p-0"
                        title="Add to Active Documents"
                      >
                        {addingDocs.has(doc.docId) ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Plus className="w-4 h-4" />
                        )}
                      </Button>
                    );
                  })()}
                  {onDocumentClick && (
                    <Button
                      size="sm"
                      variant="default"
                      onClick={() => onDocumentClick({
                        ...doc,
                        type: 'Prosecution Doc',
                        file_type: 'pros_doc',
                        resource_id: `${applicationNumber}-${doc.docId}`
                      })}
                      className="h-8 w-8 p-0"
                      title="View Document"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  )}
                  {doc.downloadOptions.pdf && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownload(doc.docId, doc.rawDoc, 'pdf')}
                      disabled={downloadingDocs.has(`${doc.docId}-pdf`)}
                      className="h-8 w-8 p-0"
                      title="Download PDF"
                    >
                      {downloadingDocs.has(`${doc.docId}-pdf`) ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setExpandedDoc(expandedDoc === doc.docId ? null : doc.docId)}
                    className="h-8 w-8 p-0"
                    title="View Details"
                  >
                    <Info className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {expandedDoc === doc.docId && (
                <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-600">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <strong>Document ID:</strong> {doc.docId}
                    </div>
                    <div>
                      <strong>Document Code:</strong> {doc.code}
                    </div>
                    {doc.mailDate && (
                      <div>
                        <strong>Mail Date:</strong> {doc.mailDate}
                      </div>
                    )}
                    {doc.pageCount && (
                      <div>
                        <strong>Page Count:</strong> {doc.pageCount}
                      </div>
                    )}
                  </div>
                  <div className="mt-2">
                    <strong>Available Formats:</strong>{' '}
                    {doc.downloadOptions.pdf && <span className="text-blue-500">PDF</span>}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
